import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ShieldCheck, CalendarClock } from "lucide-react";
import client, { errText } from "../lib/api";
import { guardCrisis } from "../lib/safety";
import iconMark from "../assets/brand/klareet-icon-white.png";
import { LAYER_ORDER } from "../components/RadarChart";
import { QuotaResetNote } from "../components/QuotaResetNote";

const LIKERT_LABELS = ["not really", "a little", "sort of", "mostly", "very much"];

function constructToLayer(construct) {
  const key = (construct || "").toLowerCase();
  if (!key) return null;
  const rules = [
    { keys: ["extra","conscien","agreeable","openness","neurotic","personality"], layer: "personality" },
    { keys: ["cognitive","reason","verbal","numeric","learn_speed","processing"], layer: "cognitive" },
    { keys: ["emotion","eq","self_aware","empathy","regulation","resilience"], layer: "emotional" },
    { keys: ["interest","curiosity","domain","affinity"], layer: "interests" },
    { keys: ["value","purpose","ethic","meaning","principle"], layer: "values" },
    { keys: ["compet","skill","capability","craft","execution"], layer: "competencies" },
    { keys: ["ready","readiness","posture","commit","focus"], layer: "readiness" },
  ];
  for (const r of rules) if (r.keys.some((k) => key.includes(k))) return r.layer;
  let h = 0; for (const c of key) h = ((h * 31) + c.charCodeAt(0)) & 0xffff;
  return LAYER_ORDER[h % LAYER_ORDER.length].key;
}

/**
 * Check in with Klair. A shorter conversation (about seven turns)
 * that ends with a delta screen instead of a full report reveal.
 */
export default function CheckinScreen({ product }) {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const noteId = params.get("note");
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [current, setCurrent] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [layersSeen, setLayersSeen] = useState(new Set());
  const [quotaBlocked, setQuotaBlocked] = useState(false);
  const [nextReset, setNextReset] = useState(null);
  const scrollRef = useRef(null);
  const base = product === "grow" ? "/grow" : "";

  const scroll = () => { setTimeout(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, 40); };

  useEffect(() => {
    const start = async () => {
      try {
        const { data } = await client.post("/demo/session/start", {
          product,
          intake: JSON.parse(localStorage.getItem("klareet.intake") || "{}"),
          mode: "checkin",
          ...(noteId ? { seed_reflection_id: noteId } : {}),
        });
        setSessionId(data.session_id);
        setCurrent(data.turn);
        const opener = (data.turn && data.turn.opener || "").trim();
        const first = [];
        if (opener) first.push({ who: "klair", text: opener, animated: true });
        first.push({ who: "klair", text: data.turn.message, animated: true });
        setMessages(first);
      } catch (e) {
        if (e?.response?.status === 429) {
          setQuotaBlocked(true);
          try {
            const { data: q } = await client.get("/klair/quota");
            setNextReset(q?.next_reset || null);
          } catch (_e) { /* the soft "tomorrow morning" fallback covers this */ }
        } else {
          setErr(errText(e, "We could not start the check in."));
        }
      }
    };
    start();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(scroll, [messages]);

  useEffect(() => {
    if (!current) return;
    const layer = constructToLayer(current.construct);
    if (!layer) return;
    setLayersSeen((prev) => { if (prev.has(layer)) return prev; const n = new Set(prev); n.add(layer); return n; });
  }, [current]);

  const submit = async (answerValue, answerText, skip) => {
    if (busy || !current) return;
    if (answerText && guardCrisis(answerText)) return;
    setBusy(true); setErr("");
    const shownText = answerText || (typeof answerValue === "number" ? LIKERT_LABELS[answerValue - 1] : (skip ? "skipped" : ""));
    if (shownText) setMessages(m => [...m, { who: "you", text: shownText }]);
    setText("");
    try {
      const { data } = await client.post("/demo/session/turn", {
        session_id: sessionId,
        answer_value: typeof answerValue === "number" ? answerValue : null,
        answer_text: answerText || null,
      });
      const nxt = data.turn;
      setCurrent(nxt);
      setMessages(m => [...m, { who: "klair", text: nxt.message, animated: true }]);
      if (nxt.done) {
        setTimeout(async () => {
          try {
            const { data: report } = await client.post("/demo/synthesise", { session_id: sessionId });
            localStorage.setItem("klareet.report", JSON.stringify(report));
            localStorage.setItem("klareet.report.delta", JSON.stringify(report.delta || null));
            nav(`${base}/checkin/delta`);
          } catch (_e) {
            setErr("We could not build your check in.");
          }
        }, 300);
      }
    } catch (e) {
      setErr(errText(e, "That did not go through."));
    } finally {
      setBusy(false);
    }
  };

  const isNarrative = current?.answer_kind === "text";
  const isLikert = current?.answer_kind === "likert5";
  const activeLayer = current ? constructToLayer(current.construct) : null;

  const total = current?.total_turns || 7;
  const idx = current?.turn_index || 0;
  const percent = current ? Math.min(100, Math.round((idx / total) * 100)) : 0;

  if (quotaBlocked) {
    return (
      <div className="k-shell k-conv relative overflow-hidden">
        <div className="k-orb k-orb-teal" style={{ top: "-6rem", right: "-4rem", width: "18rem", height: "18rem" }} />
        <div className="relative max-w-xl mx-auto px-5 pt-20 pb-24 text-center" data-testid="checkin-quota-blocked">
          <div className="w-14 h-14 rounded-2xl mx-auto mb-5 flex items-center justify-center" style={{ background: "hsl(var(--klareet-teal) / 0.14)", color: "hsl(var(--klareet-teal))" }}>
            <CalendarClock size={26} />
          </div>
          <h1 className="klareet-serif mb-2" style={{ fontSize: "clamp(1.6rem, 4vw, 2.1rem)", lineHeight: 1.15 }}>
            That is your Klair for today.
          </h1>
          <p className="k-hero-sub mb-6" style={{ lineHeight: 1.55 }}>
            You have used your check ins for today. Your allowance refreshes tomorrow, and any you do not use stack up for you, so there is no rush.
          </p>
          <div className="mb-6">
            <QuotaResetNote
              nextReset={nextReset}
              className="k-chip"
              testId="checkin-reset-countdown"
            />
          </div>
          <button onClick={() => nav(`${base}/home`)} className="k-btn k-btn-primary" data-testid="checkin-blocked-home">
            Back to your dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="k-shell k-conv relative overflow-hidden">
      <div className="k-orb k-orb-teal" style={{ top: "-6rem", right: "-4rem", width: "18rem", height: "18rem" }} />
      <div className="relative max-w-3xl mx-auto px-4 pt-8 pb-40">
        <div className="flex items-center gap-3 mb-6" data-testid="checkin-identity">
          <div className="relative w-11 h-11 rounded-[14px] flex items-center justify-center" style={{ background: "#1A202C" }}>
            <span className="k-ring-pulse" aria-hidden="true"></span>
            <img src={iconMark} alt="" aria-hidden="true" className="w-6 h-6" />
          </div>
          <div className="leading-tight">
            <div className="font-semibold">Check in with Klair</div>
            <div className="k-quiet" style={{ marginTop: -1 }} data-testid="checkin-subtitle">{noteId ? "about the note you wrote" : "a short one this time"}</div>
          </div>
          <div className="ml-auto text-right">
            <div className="k-quiet klareet-mono">{Math.min(idx + 1, total)} / {total}</div>
            <div className="text-xs" style={{ color: "hsl(var(--klareet-muted-2))" }}>saved as you go</div>
          </div>
        </div>

        <div className="k-progress-track mb-4" data-testid="checkin-progress">
          <div className="k-progress-bar" style={{ width: `${percent}%` }} />
        </div>

        {noteId && (
          <div className="k-quiet !text-xs flex items-center gap-1.5 mb-4" data-testid="checkin-note-privacy">
            <ShieldCheck size={12} /> Klair is only looking at the note you picked, nothing else.
          </div>
        )}

        <div className="flex items-center justify-between mb-6" data-testid="checkin-rail">
          <div className="k-layer-rail">
            {LAYER_ORDER.map((L) => {
              const seen = layersSeen.has(L.key);
              const active = activeLayer === L.key;
              return (
                <span
                  key={L.key}
                  className={`k-layer-dot ${active || seen ? "on" : ""}`}
                  style={{
                    background: (active || seen) ? `hsl(var(--k-${L.hue}))` : "hsl(var(--klareet-line))",
                    boxShadow: active ? `0 0 0 4px hsl(var(--k-${L.hue}) / 0.22)` : "none",
                  }}
                  title={L.label}
                  data-testid={`checkin-rail-${L.key}`}
                />
              );
            })}
          </div>
          <div className="k-quiet !text-xs">just enough to move the needle</div>
        </div>

        <div ref={scrollRef} className="space-y-4 min-h-[36vh]" data-testid="checkin-stream">
          {messages.map((m, i) => (
            <div key={i} className={m.who === "you" ? "flex justify-end" : "flex"}>
              <div className={`k-bubble ${m.who === "you" ? "k-bubble-you" : "k-bubble-klair"} ${m.animated ? "k-fade-in" : ""}`}
                   data-testid={m.who === "klair" ? "checkin-bubble-klair" : "checkin-bubble-you"}>
                {m.text}
              </div>
            </div>
          ))}
        </div>

        {err && <div className="mt-4 text-sm" style={{ color: "hsl(var(--destructive))" }} data-testid="checkin-error">{err}</div>}
      </div>

      {(isLikert || isNarrative) && (
        <div className="fixed bottom-0 left-0 right-0 border-t" style={{
          borderColor: "hsl(var(--klareet-line-soft))",
          background: "hsl(var(--background) / 0.94)",
          backdropFilter: "blur(8px)",
        }}>
          <div className="max-w-3xl mx-auto px-4 py-4">
            {isLikert && (
              <div className="grid grid-cols-5 gap-2" data-testid="checkin-likert">
                {LIKERT_LABELS.map((label, i) => (
                  <button key={i} onClick={() => submit(i + 1, null, false)} disabled={busy}
                    className="k-btn k-btn-ghost !py-2 !px-2 text-xs" data-testid={`checkin-likert-${i + 1}`}>
                    {label}
                  </button>
                ))}
              </div>
            )}
            {isNarrative && (
              <div>
                {/* KLA-018: tappable starters; tap prefills the box, typing stays first-class. */}
                {Array.isArray(current?.suggested_replies) && current.suggested_replies.length > 0 && (
                  <div className="mb-2" data-testid="checkin-suggested">
                    <div className="flex flex-wrap gap-2">
                      {current.suggested_replies.map((s, i) => (
                        <button key={i} type="button" onClick={() => setText(s)} disabled={busy} className="k-suggest k-pop-in" style={{ animationDelay: `${i * 55}ms` }} data-testid={`checkin-suggested-${i + 1}`}>{s}</button>
                      ))}
                    </div>
                    <p className="k-quiet !text-xs mt-1.5">tap one to start, or say it your own way</p>
                  </div>
                )}
                <div className="flex items-end gap-2">
                  <textarea className="k-input klareet-serif resize-none" rows={2} placeholder="a sentence or two is enough"
                    value={text} onChange={(e) => setText(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(null, text, false); } }}
                    data-testid="checkin-narrative-input" />
                  <button onClick={() => submit(null, text, false)} disabled={busy || !text.trim()}
                          className="k-btn k-btn-primary" data-testid="checkin-narrative-send">Send</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
